import { useCallback, useEffect, useRef, useState } from 'react';

export const T05_STACK_HEADER_H = 58;
/** Mindest-Fold-Range — effektiv wird die Kartenkörper-Höhe genutzt (s. getCardFoldProgress). */
const CLOSE_RANGE_PX = 190;
const FOLD_EPS = 0.004;
const DESKTOP_MQ = '(min-width: 900px)';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function measureNavOffsetPx(): number {
  const nav = document.querySelector<HTMLElement>('.t05-nav-wrap');
  return nav ? Math.round(nav.getBoundingClientRect().bottom + 8) : 88;
}

/** Dokument-Y, bei dem Karte `index` an ihrer Sticky-Dock-Position sitzt. */
function getScrollTopForSection(
  cards: NodeListOf<HTMLElement>,
  frame: HTMLElement | null,
  index: number,
  navOffset: number,
): number {
  const card = cards[index];
  if (!card) return 0;

  const dockY = navOffset + index * T05_STACK_HEADER_H;

  if (frame) {
    let flowTop = frame.getBoundingClientRect().top + window.scrollY;
    for (let j = 0; j < index; j++) {
      flowTop += cards[j].offsetHeight;
    }
    return Math.max(0, Math.round(flowTop - dockY));
  }

  const flowTop = card.getBoundingClientRect().top + window.scrollY;
  return Math.max(0, Math.round(flowTop - dockY));
}

/**
 * Fortschritt 0…1: wie weit Karte `index` durch die nächste Karte eingeklappt wird.
 * Range = Körperhöhe (Header abgezogen): Fold beginnt, sobald die nächste Karte den
 * Körper der aktuellen berührt — nicht erst in den letzten CLOSE_RANGE_PX vor dem Dock.
 */
function getCardFoldProgress(
  cards: NodeListOf<HTMLElement>,
  index: number,
  navOffset: number,
): number {
  const card = cards[index];
  const next = cards[index + 1];
  if (!next) return 0;

  const nextDock = navOffset + (index + 1) * T05_STACK_HEADER_H;
  const dist = next.getBoundingClientRect().top - nextDock;
  const foldRange = Math.max(card.offsetHeight - T05_STACK_HEADER_H, CLOSE_RANGE_PX);

  return clamp(1 - dist / foldRange, 0, 1);
}

function applyCardNavState(
  cards: NodeListOf<HTMLElement>,
  active: number,
  options: { clearClip?: boolean } = {},
): void {
  const { clearClip = true } = options;
  cards.forEach((card, index) => {
    const isExpanded = index === active;
    const isPast = index < active;

    card.classList.toggle('t05-product-stack__card--expanded', isExpanded);
    card.classList.toggle('t05-product-stack__card--past', isPast);
    card.classList.toggle('t05-product-stack__card--buried', isPast);
    card.classList.toggle('t05-product-stack__card--folding', false);
    card.classList.toggle('t05-product-stack__card--incoming', false);

    const title = card.querySelector<HTMLElement>('.t05-product-stack__title');
    if (title) title.classList.toggle('t05-product-stack__title--active', isExpanded);

    if (clearClip) {
      card.style.clipPath = '';
    }
  });
}

/** Scroll-getriebener Fold: Klassen + Clip abhängig vom Übergangsfortschritt. */
function applyScrollFoldState(
  cards: NodeListOf<HTMLElement>,
  active: number,
  navOffset: number,
  tailBottomEdge: number | null,
): void {
  cards.forEach((card, index) => {
    const progress = getCardFoldProgress(cards, index, navOffset);
    const prevProgress =
      index > 0 ? getCardFoldProgress(cards, index - 1, navOffset) : 0;
    const isFullyFolded = index < active || progress >= 1 - FOLD_EPS;
    const isFolding = progress > FOLD_EPS && progress < 1 - FOLD_EPS;
    const isExpanded = index === active && progress <= FOLD_EPS;
    const isIncoming = prevProgress > FOLD_EPS && prevProgress < 1 - FOLD_EPS;

    card.classList.toggle('t05-product-stack__card--expanded', isExpanded);
    card.classList.toggle('t05-product-stack__card--past', isFullyFolded);
    card.classList.toggle('t05-product-stack__card--buried', isFullyFolded);
    card.classList.toggle('t05-product-stack__card--folding', isFolding);
    card.classList.toggle('t05-product-stack__card--incoming', isIncoming);

    const title = card.querySelector<HTMLElement>('.t05-product-stack__title');
    if (title) title.classList.toggle('t05-product-stack__title--active', isExpanded);

    const rect = card.getBoundingClientRect();
    const maxClip = Math.max(0, rect.height - T05_STACK_HEADER_H);

    let clip = 0;
    if (index < cards.length - 1) {
      clip = Math.round(progress * maxClip);
    }
    if (tailBottomEdge != null) {
      const tailClip = Math.round(clamp(rect.bottom - tailBottomEdge, 0, maxClip));
      clip = Math.max(clip, tailClip);
    }

    card.style.clipPath = clip > 0 ? `inset(0 0 ${clip}px 0 round 20px)` : '';
  });
}

/**
 * Sticky-Karten-Stack (LaunchDarkly-Muster): Jede Karte ist selbst sticky,
 * die nächste schiebt sich darüber — nur der Header-Streifen bleibt sichtbar.
 * Keine JS-Höhenmanipulation; der Hook misst nur navOffset und activeIndex.
 */
export function useProductStack() {
  const sectionRef = useRef<HTMLElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const isNavigatingRef = useRef(false);
  const navTargetRef = useRef<number | null>(null);
  const scrollGenRef = useRef(0);
  const [navOffset, setNavOffset] = useState(88);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_MQ).matches,
  );
  const [reducedMotion, setReducedMotion] = useState(false);

  const measure = useCallback(() => {
    setNavOffset(measureNavOffsetPx());
  }, []);

  useEffect(() => {
    const mqDesktop = window.matchMedia(DESKTOP_MQ);
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    const applyDesktop = () => {
      setIsDesktop(mqDesktop.matches);
      measure();
    };
    const applyReduce = () => setReducedMotion(mqReduce.matches);

    applyDesktop();
    applyReduce();
    mqDesktop.addEventListener('change', applyDesktop);
    mqReduce.addEventListener('change', applyReduce);
    window.addEventListener('resize', measure);

    return () => {
      mqDesktop.removeEventListener('change', applyDesktop);
      mqReduce.removeEventListener('change', applyReduce);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    let raf = 0;
    let ticking = false;
    const applyMotion = isDesktop && !reducedMotion;

    const update = () => {
      ticking = false;
      const section = sectionRef.current;
      if (!section) return;
      const cards = section.querySelectorAll<HTMLElement>('[data-stack-card]');
      if (!cards.length) return;

      const navigating = isNavigatingRef.current && navTargetRef.current !== null;

      // Aktiv = höchster Index, dessen Karte an ihrer Sticky-Position angedockt ist.
      // Während Nav-Klick: Zielindex festhalten (nicht von Zwischen-Scroll überschreiben).
      let active = navigating ? navTargetRef.current! : 0;
      if (!navigating) {
        cards.forEach((card, index) => {
          const target = navOffset + index * T05_STACK_HEADER_H;
          if (card.getBoundingClientRect().top <= target + 2) active = index;
        });
      }

      if (!navigating) {
        setActiveIndex((prev) => {
          if (prev !== active) {
            activeIndexRef.current = active;
            return active;
          }
          return prev;
        });
      }

      if (!applyMotion) return;

      if (navigating) {
        applyCardNavState(cards, active, { clearClip: true });
      }

      const vh = window.innerHeight;
      cards.forEach((card, index) => {
        // 3D-Moment: Terminal-Panel kippt leicht, abhängig von der Kartenlage im Viewport.
        const visual = card.querySelector<HTMLElement>('.t05-product-stack__visual');
        if (visual) {
          const rect = card.getBoundingClientRect();
          const mid = (rect.top + rect.bottom) / 2 - vh / 2;
          const rot = clamp(mid / vh, -1, 1) * 6;
          visual.style.transform = `perspective(900px) rotateX(${rot.toFixed(2)}deg)`;
        }
      });

      // Fold-Clip nur im natürlichen Scroll — nicht während programmatischer Nav.
      if (navigating) return;

      // Abschluss-Fold über den Tail-Lauf: Karte 7 klappt 1:1 mit dem Scroll
      // zum Header-Streifen zu (nur Clip — kein Layout-Eingriff, keine Sprünge).
      let tailBottomEdge: number | null = null;
      const lastCard = cards[cards.length - 1];
      const tail = section.querySelector<HTMLElement>('[data-stack-tail]');
      if (lastCard && tail) {
        const foldable = Math.max(1, lastCard.offsetHeight - T05_STACK_HEADER_H);

        const desiredTail = foldable + 40;
        if (Math.abs(tail.offsetHeight - desiredTail) > 4) {
          tail.style.height = `${desiredTail}px`;
        }

        const postPin = document.querySelector<HTMLElement>('.t05-post-pin');
        if (postPin) {
          const desiredPull = -foldable;
          const currentPull = parseInt(postPin.style.marginTop || '0', 10);
          if (Math.abs(currentPull - desiredPull) > 4) {
            postPin.style.marginTop = `${desiredPull}px`;
          }
        }

        const lastRect = lastCard.getBoundingClientRect();
        const overlap = lastRect.bottom - tail.getBoundingClientRect().top;
        const p = clamp(overlap / foldable, 0, 1);
        tailBottomEdge = lastRect.bottom - p * foldable;
        const foldShift = Math.round(p * foldable);

        section.style.setProperty('--fold-shift', `${foldShift}px`);

        const sidebar = section.querySelector<HTMLElement>('[data-stack-sidebar]');
        if (sidebar) {
          sidebar.classList.toggle('t05-product-stack__sidebar--folding', p > 0 && p < 0.99);
          sidebar.classList.toggle('t05-product-stack__sidebar--released', p >= 0.99);
        }
      }

      applyScrollFoldState(cards, active, navOffset, tailBottomEdge);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        raf = requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
      const section = sectionRef.current;
      if (!section) return;
      section.querySelectorAll<HTMLElement>('[data-stack-card]').forEach((card) => {
        card.style.clipPath = '';
        card.classList.remove(
          't05-product-stack__card--buried',
          't05-product-stack__card--folding',
          't05-product-stack__card--incoming',
        );
        const visual = card.querySelector<HTMLElement>('.t05-product-stack__visual');
        if (visual) visual.style.transform = '';
        const title = card.querySelector<HTMLElement>('.t05-product-stack__title');
        if (title) title.classList.remove('t05-product-stack__title--active');
      });
      const tail = section.querySelector<HTMLElement>('[data-stack-tail]');
      if (tail) tail.style.height = '';
      const postPin = document.querySelector<HTMLElement>('.t05-post-pin');
      if (postPin) postPin.style.marginTop = '';
      section.style.removeProperty('--fold-shift');
      const sidebar = section.querySelector<HTMLElement>('[data-stack-sidebar]');
      if (sidebar) {
        sidebar.classList.remove(
          't05-product-stack__sidebar--folding',
          't05-product-stack__sidebar--released',
        );
      }
    };
  }, [navOffset, isDesktop, reducedMotion]);

  const resetStackFoldState = useCallback((section: HTMLElement) => {
    const cards = section.querySelectorAll<HTMLElement>('[data-stack-card]');
    cards.forEach((card) => {
      card.style.clipPath = '';
      card.classList.remove(
        't05-product-stack__card--buried',
        't05-product-stack__card--past',
        't05-product-stack__card--expanded',
        't05-product-stack__card--folding',
        't05-product-stack__card--incoming',
      );
      const visual = card.querySelector<HTMLElement>('.t05-product-stack__visual');
      if (visual) visual.style.transform = '';
      const title = card.querySelector<HTMLElement>('.t05-product-stack__title');
      if (title) title.classList.remove('t05-product-stack__title--active');
    });

    const tail = section.querySelector<HTMLElement>('[data-stack-tail]');
    if (tail) tail.style.height = '';

    const postPin = document.querySelector<HTMLElement>('.t05-post-pin');
    if (postPin) postPin.style.marginTop = '';

    section.style.removeProperty('--fold-shift');

    const sidebar = section.querySelector<HTMLElement>('[data-stack-sidebar]');
    if (sidebar) {
      sidebar.classList.remove(
        't05-product-stack__sidebar--folding',
        't05-product-stack__sidebar--released',
      );
    }
  }, []);

  const finishProgrammaticScroll = useCallback((gen: number, expectedIndex: number) => {
    if (gen !== scrollGenRef.current) return;

    isNavigatingRef.current = false;
    navTargetRef.current = null;

    const section = sectionRef.current;
    if (!section) return;

    const cards = section.querySelectorAll<HTMLElement>('[data-stack-card]');
    if (!cards.length) return;

    resetStackFoldState(section);

    const frame = frameRef.current;
    const targetY = getScrollTopForSection(cards, frame, expectedIndex, navOffset);
    const landed = Math.abs(window.scrollY - targetY) <= 6;

    let active = expectedIndex;
    if (!landed) {
      active = 0;
      cards.forEach((card, i) => {
        const dock = navOffset + i * T05_STACK_HEADER_H;
        if (card.getBoundingClientRect().top <= dock + 2) active = i;
      });
    }

    activeIndexRef.current = active;
    setActiveIndex(active);
    applyCardNavState(cards, active);
  }, [navOffset, resetStackFoldState]);

  const scrollToSection = useCallback(
    (index: number) => {
      const section = sectionRef.current;
      if (!section) return;
      const cards = section.querySelectorAll<HTMLElement>('[data-stack-card]');
      const card = cards[index];
      if (!card) return;

      const clamped = Math.min(cards.length - 1, Math.max(0, index));
      const goingBack = clamped < activeIndexRef.current;

      // 1) Fold/Clip sofort zurücksetzen, bevor Scroll-Ziel gemessen wird.
      resetStackFoldState(section);

      // 2) Nav-Ziel sofort setzen — React-Klassen + Sidebar folgen dem Klick.
      scrollGenRef.current += 1;
      const gen = scrollGenRef.current;
      isNavigatingRef.current = true;
      navTargetRef.current = clamped;
      activeIndexRef.current = clamped;
      setActiveIndex(clamped);
      applyCardNavState(cards, clamped);

      const frame = frameRef.current;
      const target = getScrollTopForSection(cards, frame, clamped, navOffset);

      const releaseNav = () => finishProgrammaticScroll(gen, clamped);

      if (reducedMotion || !isDesktop) {
        window.scrollTo({ top: target, behavior: 'auto' });
        requestAnimationFrame(() => {
          requestAnimationFrame(releaseNav);
        });
        return;
      }

      window.scrollTo({
        top: target,
        behavior: goingBack ? 'auto' : 'smooth',
      });

      if (goingBack) {
        requestAnimationFrame(() => {
          requestAnimationFrame(releaseNav);
        });
        return;
      }

      let done = false;
      const finish = () => {
        if (done || gen !== scrollGenRef.current) return;
        done = true;
        window.removeEventListener('scrollend', onScrollEnd);
        releaseNav();
      };

      const onScrollEnd = () => finish();

      if ('onscrollend' in (window as object)) {
        window.addEventListener('scrollend', onScrollEnd, { once: true });
        window.setTimeout(finish, 1200);
        return;
      }

      let lastY = window.scrollY;
      let stableFrames = 0;
      let pollRaf = 0;

      const poll = () => {
        if (gen !== scrollGenRef.current) return;
        const y = window.scrollY;
        if (Math.abs(y - lastY) < 1) {
          stableFrames += 1;
          if (stableFrames >= 4) {
            finish();
            return;
          }
        } else {
          stableFrames = 0;
          lastY = y;
        }
        pollRaf = requestAnimationFrame(poll);
      };
      pollRaf = requestAnimationFrame(poll);
      window.setTimeout(() => {
        cancelAnimationFrame(pollRaf);
        finish();
      }, 1200);
    },
    [finishProgrammaticScroll, isDesktop, navOffset, reducedMotion, resetStackFoldState],
  );

  return {
    sectionRef,
    frameRef,
    activeIndex,
    navOffset,
    isDesktop,
    reducedMotion,
    scrollToSection,
  };
}
